import { createBooking, getAvailabilityData, triggerBookingDeposit } from '../api/bookings.js';
import { getServices, getCategories } from '../api/services.js';

let selectedCatalogItems = [];
let availableServices = [];
let selectedCategory = 'All';
let catalogSearchQuery = '';

export async function renderBookingForm(container, options = {}) {
  selectedCatalogItems = [];
  catalogSearchQuery = '';
  selectedCategory = 'All';

  // 1. Concurrent Ingress: Fetch Live Blocked Dates & Active Catalog
  const [{ blockedDates }, catalogServices, categories] = await Promise.all([
    getAvailabilityData().catch(() => ({ blockedDates: [] })),
    getServices().catch(() => []),
    getCategories().catch(() => ['All'])
  ]);

  availableServices = catalogServices;

  // 2. Parse Deep-Linking URL Query Parameters (Gumtree Ad Campaign Integration)
  const urlParams = new URLSearchParams(window.location.search);
  const targetCategory = urlParams.get('cat') || urlParams.get('category');
  const targetService = urlParams.get('service') || urlParams.get('item');

  if (targetCategory) {
    const matchedCategory = categories.find(
      c => c.toLowerCase() === targetCategory.trim().toLowerCase()
    );
    if (matchedCategory) {
      selectedCategory = matchedCategory;
    }
  }

  if (targetService) {
    const cleanQuery = targetService.toLowerCase().replace(/[^a-z0-9]/g, '');
    const matchedService = availableServices.find(s => {
      const cleanName = (s.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      return cleanName.includes(cleanQuery) || cleanQuery.includes(cleanName);
    });

    if (matchedService) {
      selectedCatalogItems.push({
        id: matchedService.id,
        name: matchedService.name,
        price: matchedService.priceTotal,
        quantity: 1
      });
      if (matchedService.category && selectedCategory === 'All') {
        selectedCategory = matchedService.category;
      }
    }
  }

  // 3. Operational Date Scheduling Defaults
  const todayStr = new Date().toISOString().split('T')[0];
  const nextAvailableDateStr = calculateNextAvailableDate(blockedDates);
  const selectedBookingDate = nextAvailableDateStr || '';

  // 4. Render Layout
  container.innerHTML = `
    <div class="bg-slate-900/80 border border-slate-800 p-5 sm:p-6 rounded-3xl space-y-6 shadow-xl max-w-4xl mx-auto">
      <div class="border-b border-slate-800 pb-4">
        <h2 class="text-xl sm:text-2xl font-black text-white tracking-tight">Direct Service Booking</h2>
        <p class="text-xs text-slate-400 mt-1">Locked standard rates. Choose your services and reserve your slot.</p>
      </div>

      <form id="quick-booking-form" class="space-y-6">
        <div id="booking-alert" class="hidden p-4 rounded-2xl text-xs"></div>

        <!-- CONTACT DETAILS -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div class="space-y-1">
            <label class="text-xs font-semibold text-slate-300">Your Full Name *</label>
            <input type="text" name="customerName" class="input-dark" placeholder="e.g. Alex Smith" required />
          </div>
          <div class="space-y-1">
            <label class="text-xs font-semibold text-slate-300">Contact Phone Number *</label>
            <input type="tel" name="customerPhone" class="input-dark" placeholder="e.g. 07700 900000" required />
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div class="space-y-1">
            <label class="text-xs font-semibold text-slate-300">Email Address (For Invoice & Deposit) *</label>
            <input type="email" name="customerEmail" class="input-dark" placeholder="e.g. client@example.com" required />
          </div>
          <div class="space-y-1">
            <label class="text-xs font-semibold text-slate-300">Property / Job Site Address *</label>
            <input type="text" name="jobAddress" class="input-dark" placeholder="e.g. Flat 4, 12 Example Way, Salford, M99 1AA" required />
          </div>
        </div>

        <!-- DATE & TIME SELECTION -->
        <div class="space-y-3 pt-2 border-t border-slate-800">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div class="flex items-center gap-2">
              <span class="text-xs font-semibold text-slate-300">Preferred Date *</span>
              <span id="selected-date-badge" class="${selectedBookingDate ? '' : 'hidden'} text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2.5 py-0.5 rounded-full">
                Selected: ${formatNiceDate(selectedBookingDate)}
              </span>
            </div>
            <button type="button" id="toggle-availability-drawer" class="text-xs text-blue-400 hover:text-blue-300 font-semibold flex items-center gap-1 transition">
              <span>📅 View Live Availability</span>
              <span id="drawer-arrow">▴</span>
            </button>
          </div>

          <!-- AVAILABILITY DRAWER (OPEN BY DEFAULT) -->
          <div id="availability-drawer" class="bg-slate-950 p-3.5 rounded-2xl border border-slate-800 space-y-2">
            <div class="flex justify-between items-center text-xs">
              <span class="font-bold text-slate-300">Upcoming Availability (28 Days)</span>
              <span class="text-[10px] text-slate-400">Click an available date to select</span>
            </div>
            <div id="availability-grid" class="grid grid-cols-4 sm:grid-cols-7 gap-1.5 pt-1"></div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- UK FORMAT DISPLAY WITH NATIVE PICKER OVERLAY -->
            <div class="space-y-1">
              <div class="relative flex items-center">
                <input 
                  type="text" 
                  id="bookingDateDisplay" 
                  class="input-dark font-mono cursor-pointer pr-10" 
                  placeholder="DD/MM/YYYY" 
                  value="${formatUKDate(selectedBookingDate)}" 
                  readonly 
                  required 
                />
                <span class="absolute right-3 text-slate-400 pointer-events-none text-sm">📅</span>
                <input 
                  type="date" 
                  id="bookingDate" 
                  name="bookingDate" 
                  class="absolute inset-0 opacity-0 cursor-pointer w-full h-full" 
                  min="${todayStr}" 
                  value="${selectedBookingDate}" 
                  required 
                />
              </div>
            </div>

            <!-- TIME SLOTS STARTING AT 11:00 AM -->
            <div class="space-y-1">
              <select name="bookingTime" class="input-dark" required>
                <option value="11:00:00" selected>11:00 AM - Morning Slot</option>
                <option value="13:00:00">01:00 PM - Early Afternoon</option>
                <option value="15:00:00">03:00 PM - Late Afternoon</option>
                <option value="17:00:00">05:00 PM - Evening Slot</option>
              </select>
            </div>
          </div>
        </div>

        <!-- SERVICES SELECTION CATALOG -->
        <div class="space-y-3 pt-4 border-t border-slate-800">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div>
              <label class="text-xs font-semibold text-slate-300 block">Select Services (Locked Standard Pricing)</label>
              <span class="text-[10px] text-slate-400">Click 'Add' to include items in your dispatch request.</span>
            </div>
            <span id="quote-running-total" class="text-base text-emerald-400 font-black">Est. Total: £0.00</span>
          </div>

          <!-- FULL-WIDTH TITLE SEARCH BOX -->
          <div class="relative w-full">
            <input 
              type="text" 
              id="catalog-search" 
              placeholder="🔍 Search service titles (e.g. door, lock, paint)..." 
              class="input-dark text-xs py-2.5 pl-3 pr-9 w-full"
              value="${catalogSearchQuery}"
            />
            <button 
              type="button" 
              id="clear-catalog-search" 
              class="${catalogSearchQuery ? '' : 'hidden'} absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white text-xs font-bold p-1 transition"
              title="Clear search"
            >✕</button>
          </div>

          <!-- CATEGORY FILTER BUTTONS -->
          <div class="flex gap-1.5 overflow-x-auto pb-1" id="catalog-category-tabs">
            ${categories.map(cat => `
              <button type="button" data-category="${cat}" class="cat-tab-btn text-xs px-3 py-1.5 rounded-lg ${cat === selectedCategory ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'} transition border border-slate-700 whitespace-nowrap">
                ${cat}
              </button>
            `).join('')}
          </div>

          <!-- SERVICES GRID -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[340px] overflow-y-auto pr-1" id="catalog-grid"></div>
        </div>

        <!-- DEPOSIT NOTICE BANNER -->
        <div class="p-3.5 bg-blue-500/10 border border-blue-500/30 rounded-2xl text-xs text-blue-300 flex items-center gap-2.5">
          <span class="text-base flex-shrink-0">💳</span>
          <span class="leading-relaxed">To secure your day, a deposit request has been sent to you. Otherwise, an engineer will contact you regarding availability on that day.</span>
        </div>

        <button type="submit" id="submit-btn" class="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3.5 rounded-xl transition shadow-lg shadow-blue-600/30 text-xs sm:text-sm tracking-wide">
          Confirm & Dispatch Booking
        </button>
      </form>
    </div>
  `;

  renderCatalogGrid(container);

  const form = container.querySelector('#quick-booking-form');
  const alertBox = container.querySelector('#booking-alert');
  const submitBtn = container.querySelector('#submit-btn');
  const dateInput = form.querySelector('#bookingDate');
  const dateDisplayInput = form.querySelector('#bookingDateDisplay');
  const searchInput = container.querySelector('#catalog-search');
  const clearSearchBtn = container.querySelector('#clear-catalog-search');

  let previousValidDate = dateInput.value;

  const updateSelectedDateBadge = (dateStr) => {
    const badge = container.querySelector('#selected-date-badge');
    if (!badge) return;
    if (dateStr) {
      badge.textContent = `Selected: ${formatNiceDate(dateStr)}`;
      badge.classList.remove('hidden');
    } else {
      badge.classList.add('hidden');
    }
  };

  renderAvailabilityDrawer(container, blockedDates, (selectedDate) => {
    dateInput.value = selectedDate;
    if (dateDisplayInput) {
      dateDisplayInput.value = formatUKDate(selectedDate);
    }
    previousValidDate = selectedDate;
    updateSelectedDateBadge(selectedDate);
    alertBox.classList.add('hidden');
  });

  const drawerBtn = container.querySelector('#toggle-availability-drawer');
  const drawer = container.querySelector('#availability-drawer');
  const arrow = container.querySelector('#drawer-arrow');

  drawerBtn.addEventListener('click', () => {
    const isHidden = drawer.classList.contains('hidden');
    drawer.classList.toggle('hidden', !isHidden);
    arrow.textContent = isHidden ? '▴' : '▾';
  });

  dateInput.addEventListener('change', (e) => {
    const selected = e.target.value;
    if (blockedDates.includes(selected)) {
      alertBox.className = 'p-3 rounded-xl text-xs bg-red-500/10 text-red-400 border border-red-500/30';
      alertBox.textContent = `Date ${formatUKDate(selected) || selected} is fully booked. Selection reverted to ${formatUKDate(previousValidDate) || 'valid date'}.`;
      alertBox.classList.remove('hidden');
      e.target.value = previousValidDate || '';
      if (dateDisplayInput) {
        dateDisplayInput.value = formatUKDate(previousValidDate);
      }
      updateSelectedDateBadge(previousValidDate);
    } else {
      alertBox.classList.add('hidden');
      previousValidDate = selected;
      if (dateDisplayInput) {
        dateDisplayInput.value = formatUKDate(selected);
      }
      updateSelectedDateBadge(selected);
    }
  });

  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      catalogSearchQuery = e.target.value.trim();
      if (clearSearchBtn) {
        clearSearchBtn.classList.toggle('hidden', !catalogSearchQuery);
      }
      renderCatalogGrid(container);
    });
  }

  if (clearSearchBtn) {
    clearSearchBtn.addEventListener('click', () => {
      catalogSearchQuery = '';
      if (searchInput) searchInput.value = '';
      clearSearchBtn.classList.add('hidden');
      renderCatalogGrid(container);
      searchInput?.focus();
    });
  }

  container.querySelectorAll('.cat-tab-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      selectedCategory = e.target.dataset.category;
      container.querySelectorAll('.cat-tab-btn').forEach(b => {
        const isActive = b.dataset.category === selectedCategory;
        b.className = `cat-tab-btn text-xs px-3 py-1.5 rounded-lg ${isActive ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'} transition border border-slate-700 whitespace-nowrap`;
      });
      renderCatalogGrid(container);
    });
  });

  // SUBMIT HANDLER
  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    if (selectedCatalogItems.length === 0) {
      alertBox.className = 'p-3 rounded-xl text-xs bg-red-500/10 text-red-400 border border-red-500/30';
      alertBox.textContent = 'Please select at least one service from the catalog before proceeding.';
      alertBox.classList.remove('hidden');
      return;
    }

    const scheduledDateTime = `${form.bookingDate.value}T${form.bookingTime.value}`;
    const items = selectedCatalogItems.map(i => ({
      service_name: i.name,
      quantity: i.quantity
    }));

    const customerEmail = form.customerEmail.value.trim().toLowerCase();
    const customerName = form.customerName.value.trim();

    try {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Dispatching Booking...';

      const res = await createBooking({
        customerName,
        customerEmail,
        customerPhone: form.customerPhone.value,
        jobAddress: form.jobAddress.value,
        scheduledDateTime,
        items
      });

      const newBookingId = res.booking_id || res.id || '';

      // Decoupled Background Deposit Trigger
      if (newBookingId && customerEmail) {
        triggerBookingDeposit(newBookingId, customerEmail).catch(err => {
          console.warn('⚠️ Quick Bookings deposit trigger error:', err);
        });
      }

      // Friendly Confirmation Screen
      container.innerHTML = `
        <div class="bg-slate-900/90 border border-emerald-500/30 p-8 rounded-3xl max-w-xl mx-auto text-center space-y-5 shadow-2xl">
          <div class="w-16 h-16 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-2xl flex items-center justify-center text-3xl mx-auto">
            🎉
          </div>
          <div class="space-y-2">
            <h2 class="text-2xl font-black text-white">Booking #${newBookingId} Confirmed!</h2>
            <p class="text-xs text-slate-400 leading-relaxed max-w-md mx-auto">
              Your appointment has been scheduled for <b class="text-slate-200">${formatNiceDate(form.bookingDate.value)}</b> (${formatUKDate(form.bookingDate.value)}).
            </p>
          </div>
          <div class="p-4 bg-slate-950 rounded-2xl border border-slate-800 text-xs text-left text-slate-300 space-y-1.5 font-mono">
            <div><span class="text-slate-500">Site:</span> ${form.jobAddress.value}</div>
            <div><span class="text-slate-500">Contact:</span> ${customerName} (${form.customerPhone.value})</div>
            <div><span class="text-slate-500">Scope:</span> ${items.map(i => `${i.service_name} (x${i.quantity})`).join(', ')}</div>
          </div>
          <p class="text-xs text-emerald-400/90">
            A confirmation and deposit receipt have been dispatched to <b>${customerEmail}</b>.
          </p>
          <div class="pt-2">
            <button type="button" onclick="window.location.reload();" class="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl text-xs transition shadow-lg shadow-blue-600/30">
              Schedule Another Service
            </button>
          </div>
        </div>
      `;

      if (typeof options.onSuccess === 'function') {
        options.onSuccess(newBookingId);
      }

    } catch (err) {
      alertBox.className = 'p-3 rounded-xl text-xs bg-red-500/10 text-red-400 border border-red-500/30';
      alertBox.textContent = err.message || 'Failed to dispatch booking.';
      alertBox.classList.remove('hidden');
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Confirm & Dispatch Booking';
    }
  });
}

function calculateNextAvailableDate(blockedDates) {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  for (let i = 0; i < 45; i++) {
    const str = d.toISOString().split('T')[0];
    if (!blockedDates.includes(str)) return str;
    d.setDate(d.getDate() + 1);
  }
  return null;
}

function formatNiceDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function formatUKDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  if (!y || !m || !d) return dateStr;
  return `${d}/${m}/${y}`;
}

function matchesSearch(service, query) {
  if (!query) return true;
  const title = (service.name || '').toLowerCase();
  const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  return tokens.every(token => title.includes(token));
}

function renderCatalogGrid(container) {
  const grid = container.querySelector('#catalog-grid');
  if (!grid) return;

  const filtered = availableServices.filter(s => {
    const matchesCategory = selectedCategory === 'All' || s.category === selectedCategory;
    const matchesQuery = matchesSearch(s, catalogSearchQuery);
    return matchesCategory && matchesQuery;
  });

  if (filtered.length === 0) {
    grid.innerHTML = `
      <div class="col-span-full p-6 text-center text-slate-400 text-xs bg-slate-950/40 rounded-2xl border border-slate-800/60">
        No services match "${catalogSearchQuery || selectedCategory}".
      </div>
    `;
    return;
  }

  grid.innerHTML = filtered.map(service => {
    const selected = selectedCatalogItems.find(i => i.id === service.id);
    const qty = selected ? selected.quantity : 0;

    return `
      <div class="bg-slate-950/80 p-3 rounded-2xl border ${qty > 0 ? 'border-blue-500 bg-blue-950/20' : 'border-slate-800'} flex flex-col justify-between space-y-2 transition">
        <div class="flex gap-3 items-start">
          ${service.imageUrl ? `
            <img src="${service.imageUrl}" alt="${service.name}" class="w-12 h-12 rounded-xl object-cover border border-slate-800 flex-shrink-0" />
          ` : `
            <div class="w-12 h-12 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-lg flex-shrink-0">🛠️</div>
          `}
          <div class="space-y-0.5 flex-1 min-w-0">
            <h4 class="font-bold text-xs text-white truncate">${service.name}</h4>
            <p class="text-[10px] text-slate-400 line-clamp-2">${service.description || 'Standard service offering.'}</p>
          </div>
        </div>

        <div class="flex items-center justify-between pt-2 border-t border-slate-800/80">
          <span class="text-xs font-bold text-emerald-400">£${Number(service.priceTotal).toFixed(2)}</span>
          <div class="flex items-center gap-1.5">
            ${qty > 0 ? `
              <button type="button" data-id="${service.id}" class="btn-qty-dec w-6 h-6 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs text-white font-bold">-</button>
              <span class="text-xs font-bold text-blue-400 px-1">${qty}</span>
            ` : ''}
            <button type="button" data-id="${service.id}" class="btn-qty-inc px-2.5 py-1 rounded-lg ${qty > 0 ? 'bg-blue-600 text-white' : 'bg-slate-800 text-blue-400 hover:bg-slate-700'} text-xs font-semibold transition">
              ${qty > 0 ? '+' : 'Add'}
            </button>
          </div>
        </div>
      </div>
    `;
  }).join('');

  updateCartTotal(container);

  grid.querySelectorAll('.btn-qty-inc').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const id = e.target.dataset.id;
      const service = availableServices.find(s => s.id == id);
      if (!service) return;

      const existing = selectedCatalogItems.find(i => i.id == id);
      if (existing) {
        existing.quantity += 1;
      } else {
        selectedCatalogItems.push({ id: service.id, name: service.name, price: service.priceTotal, quantity: 1 });
      }
      renderCatalogGrid(container);
    });
  });

  grid.querySelectorAll('.btn-qty-dec').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const id = e.target.dataset.id;
      const index = selectedCatalogItems.findIndex(i => i.id == id);
      if (index > -1) {
        if (selectedCatalogItems[index].quantity > 1) {
          selectedCatalogItems[index].quantity -= 1;
        } else {
          selectedCatalogItems.splice(index, 1);
        }
      }
      renderCatalogGrid(container);
    });
  });
}

function updateCartTotal(container) {
  const totalDisplay = container.querySelector('#quote-running-total');
  if (!totalDisplay) return;

  const total = selectedCatalogItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  totalDisplay.textContent = `Est. Total: £${total.toFixed(2)}`;
}

function renderAvailabilityDrawer(container, blockedDates, onSelectDate) {
  const grid = container.querySelector('#availability-grid');
  if (!grid) return;

  const d = new Date();
  d.setDate(d.getDate() + 1);
  let html = '';

  for (let i = 0; i < 28; i++) {
    const dateStr = d.toISOString().split('T')[0];
    const dayNum = d.getDate();
    const dayName = d.toLocaleDateString('en-GB', { weekday: 'narrow' });
    const monthShort = d.toLocaleDateString('en-GB', { month: 'short' });
    const isBlocked = blockedDates.includes(dateStr);

    if (isBlocked) {
      html += `
        <div class="p-1.5 rounded-lg bg-rose-500/10 border border-rose-500/20 text-center text-[10px] text-rose-400 line-through opacity-60 cursor-not-allowed">
          <span class="block text-[8px] font-mono text-rose-500/80">${dayName}</span>
          <span class="font-bold">${dayNum} ${monthShort}</span>
        </div>
      `;
    } else {
      html += `
        <button type="button" data-date="${dateStr}" class="btn-avail-date p-1.5 rounded-lg bg-slate-900 hover:bg-emerald-600/30 border border-slate-800 hover:border-emerald-500 text-center text-[10px] text-emerald-400 hover:text-white transition font-medium">
          <span class="block text-[8px] font-mono text-slate-400">${dayName}</span>
          <span class="font-bold">${dayNum} ${monthShort}</span>
        </button>
      `;
    }

    d.setDate(d.getDate() + 1);
  }

  grid.innerHTML = html;

  grid.querySelectorAll('.btn-avail-date').forEach(btn => {
    btn.addEventListener('click', (e) => {
      onSelectDate(e.currentTarget.dataset.date);
    });
  });
}